/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import PropTypes from "prop-types"; // Npm: Prop types for checking props type.
import Moment from "moment"; // Npm: Moment.js library for date and time formatting.
import _ from "underscore"; // Npm: utility module.
import { HiArrowPath, HiPlusCircle } from "react-icons/hi2"; // Npm: React icons.
import { connect } from "react-redux"; // Npm: React Redux for state management.
import { useLocation } from "react-router-dom"; // Npm: React Router hooks.
import { useQuery } from "@apollo/client"; // Npm: Apollo client for GraphQL queries and mutations.
import { DataGrid } from "@mui/x-data-grid"; // Npm: Material UI Data Grid.
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import TableSpinner from "components/TableSpinner";
import DownloadToExcel from "components/DownloadToExcel";
import Modal from "components/Modal";
import VendorAccountAttachRoutePlan from "components/VendorAccountAttachRoutePlan";
import RoutePlanUpsert from "components/RoutePlanUpsert";
import RoutePlanDelete from "components/RoutePlanDelete";
import RoutePlanDropDownOptions from "components/RoutePlanDropDownOptions";
import { MemoizedInput } from "components/MemoizedInput";

/*
 * GRAPHS
 */
import RoutePlanReadQuery from "./__query__/index.routePlan.read.query";

/*
 * STYLES
 */
import { buttonStyle } from "./index.style";

/*
 * OBJECTS
 */
const Index = ({ passOn, PassOnUpdate }) => {
  const _locationRef = useLocation();
  const _routeIdFromUrl = React.useMemo(
    () => new URLSearchParams(_locationRef.search).get("routeId"),
    [_locationRef.search],
  );
  const _typeFromUrl = React.useMemo(
    () => new URLSearchParams(_locationRef.search).get("type"),
    [_locationRef.search],
  );
  const _routeId = _routeIdFromUrl ?? passOn?.routeId;

  // Local variable.
  let _rows;

  // Const assignment.
  const _successFlags =
    Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(
      (i) => i.key,
    );

  // Hook assignment.
  const [selectAll, setSelectAll] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });
  const _setFilterDebounced = React.useMemo(
    () => _.debounce(setFilterModel, 500),
    [],
  );
  const [isRefreshing, setIsRefreshing] = React.useState(false)

  // Debounced search states
  const [mccNameToSearch, setMccNameToSearch] = React.useState("");
  const [mncNameToSearch, setMncNameToSearch] = React.useState("");
  const [countryNameToSearch, setCountryNameToSearch] = React.useState("");
  const [operatorNameToSearch, setOperatorNameToSearch] = React.useState("");

  React.useEffect(() => {
    if (_.isEmpty(_routeIdFromUrl)) return void 0;

    PassOnUpdate({
      routeId: _routeIdFromUrl,
      ...(_.isEmpty(_typeFromUrl) ? {} : { type: _typeFromUrl }),
    });
  }, [_routeIdFromUrl, _typeFromUrl, PassOnUpdate]);

  const {
    isOpen: isRoutePlanCreateOpen,
    onOpen: onRoutePlanCreateOpen,
    onClose: onRoutePlanCreateClose,
  } = useDisclosure();
  const {
    isOpen: isRoutePlanUpdateOpen,
    onOpen: onRoutePlanUpdateOpen,
    onClose: onRoutePlanUpdateClose,
  } = useDisclosure();
  const {
    isOpen: isRoutePlanDeleteOpen,
    onOpen: onRoutePlanDeleteOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();
  const {
    isOpen: isVendorAccountAttachRoutePlanOpen,
    onOpen: onVendorAccountAttachRoutePlanOpen,
    onClose: onVendorAccountAttachRoutePlanClose,
  } = useDisclosure();
  const _isPreviousPageRef = React.useRef(false);
  const _paginationModelRef = React.useRef(paginationModel);
  const _rowCountRef = React.useRef(0);

  const _ResetPagination = React.useCallback(() => {
    _isPreviousPageRef.current = false;
    setPaginationModel((current) => {
      const nextPaginationModel = { ...current, page: 0 };

      _paginationModelRef.current = nextPaginationModel;

      return nextPaginationModel;
    });
  }, []);

  // Debounce handlers
  const _onMccNameToSearchInputChange = React.useRef(
    _.debounce((e) => {
      _ResetPagination();
      setMccNameToSearch(e.target.value);
    }, 800),
  ).current;
  const _onMncNameToSearchInputChange = React.useRef(
    _.debounce((e) => {
      _ResetPagination();
      setMncNameToSearch(e.target.value);
    }, 800),
  ).current;
  const _onOperatorNameToSearchInputChange = React.useRef(
    _.debounce((e) => {
      _ResetPagination();
      setOperatorNameToSearch(e.target.value);
    }, 800),
  ).current;
  const _onCountryNameToSearchInputChange = React.useRef(
    _.debounce((e) => {
      _ResetPagination();
      setCountryNameToSearch(e.target.value);
    }, 800),
  ).current;

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      _onMccNameToSearchInputChange.cancel?.();
      _onMncNameToSearchInputChange.cancel?.();
      _onOperatorNameToSearchInputChange.cancel?.();
      _onCountryNameToSearchInputChange.cancel?.();
      _setFilterDebounced.cancel?.();
    };
  }, [_onCountryNameToSearchInputChange, _onMccNameToSearchInputChange, _onMncNameToSearchInputChange, _onOperatorNameToSearchInputChange, _setFilterDebounced]);

  const _isFilterApplied = filterModel?.items?.some(
    (i) => i.value != void 0 && i.value !== "",
  );
  const _appliedFilter = _isFilterApplied ? filterModel : void 0;
  const _QueryRoutePlanReadVariables = React.useMemo(
    () => ({
      routeId: _routeId,
      displayName: _routeId?.displayName,
      mccNameToSearch,
      mncNameToSearch,
      operatorNameToSearch,
      countryNameToSearch,
      filter: _appliedFilter,
      skip: paginationModel.pageSize * paginationModel.page,
      take: paginationModel.pageSize,
    }),
    [
      _appliedFilter,
      countryNameToSearch,
      mccNameToSearch,
      mncNameToSearch,
      operatorNameToSearch,
      paginationModel.page,
      paginationModel.pageSize,
      _routeId,
    ],
  );

  const _QueryRoutePlanRead = useQuery(RoutePlanReadQuery, {
    variables: _QueryRoutePlanReadVariables,
    skip: _.isEmpty(_routeId),
    fetchPolicy: _isPreviousPageRef.current
      ? "cache-first"
      : Object.React.App.fetchPolicy,
  });

  const { refetch } = _QueryRoutePlanRead;

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true)

    try {
      await refetch()
    } finally {
      setIsRefreshing(false)
    }
  }, [refetch])
  const _HandlePaginationModelChange = React.useCallback(
    (__paginationModel) => {
      // Only backward pagination with the same page size should prefer cached rows.
      _isPreviousPageRef.current =
        __paginationModel.page < _paginationModelRef.current.page &&
        __paginationModel.pageSize === _paginationModelRef.current.pageSize;

      _paginationModelRef.current = __paginationModel;
      setPaginationModel(__paginationModel);
    },
    [],
  );
  const _HandleFilterModelChange = React.useCallback(
    (__filterModel) => {
      _ResetPagination();
      _setFilterDebounced(__filterModel);
    },
    [_ResetPagination, _setFilterDebounced],
  );

  const _tableHeaderHeightRef = React.useRef(0);
  const _isFirstLoadCompleted = React.useRef(false);
  const _isCurrentViewMobile = useBreakpointValue(
    { base: true, md: false },
    { fallback: "md" },
  ) ?? false;

  // Event handler.
  React.useEffect(() => {
    // Reset selectedIds when the data is re-fetched
    if (_QueryRoutePlanRead.data) {
      // Reset states.
      setSelectedIds([]);
      setSelectAll(false);
    }
  }, [
    isRoutePlanCreateOpen,
    isRoutePlanUpdateOpen,
    isRoutePlanDeleteOpen,
    isVendorAccountAttachRoutePlanOpen,
    _QueryRoutePlanRead.data,
  ]);

  // Object assignment.
  const _HandleCheckboxChange = (id) => {
    // Update state.
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const _HandleSelectAll = () => {
    // Update state.
    setSelectAll(!selectAll);
    setSelectedIds(
      selectAll
        ? []
        : _QueryRoutePlanRead?.data?.RoutePlanRead?.map((item) => item.id) ||
            [],
    );
  };

  // Data assignment
  if (
    !_QueryRoutePlanRead.loading &&
    0 < _QueryRoutePlanRead.data?.RoutePlanRead?.length
  ) {
    _isFirstLoadCompleted.current = true;
  }

  if (
    0 === _QueryRoutePlanRead.data?.RoutePlanRead?.length ||
    (0 < _QueryRoutePlanRead.data?.RoutePlanRead?.length &&
      (!_.every(
        _.pluck(_QueryRoutePlanRead.data?.RoutePlanRead, "status"),
        (i) => _successFlags?.includes(i),
      ) ||
        _.some(
          _.pluck(_QueryRoutePlanRead.data?.RoutePlanRead, "status"),
          (i) => "ROUTE_PLAN_NOT_FOUND" === i,
        )))
  ) {
    _isFirstLoadCompleted.current = false;
  }

  // Update rows.
  _rows = _QueryRoutePlanRead.data?.RoutePlanRead;

  // Columns definition
  const columns = React.useMemo(
    () => [
      {
        field: "checkbox",
        headerName: "",
        width: 60,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        renderHeader: () => (
          <Checkbox
            bg="gray.300"
            onChange={_HandleSelectAll}
            isChecked={selectAll}
          />
        ),
        renderCell: ({ row }) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Checkbox
              bg="gray.300"
              isChecked={selectedIds.includes(row.id)}
              onChange={() => _HandleCheckboxChange(row.id)}
            />
          </Box>
        ),
      },

      {
        field: "sno",
        headerName: "S No.",
        align: "center",
        headerAlign: "center",
        width: 80,
        sortable: false,
        filterable: false,
      },
      {
        field: "id",
        headerName: "ID",
        align: "center",
        headerAlign: "center",
        hide: true,
      },
      {
        field: "Route.displayName",
        headerName: "Route Name",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "VendorAccount.displayName",
        headerName: "Vendor Account",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "Mcc.countryName",
        headerName: "Country Name",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "Mnc.network",
        headerName: "Operator",
        editable: false,
        sortable: false,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 250,
      },
      {
        field: "Mcc.mcc",
        headerName: "Mcc",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        width: 150,
      },
      {
        field: "Mnc.mnc",
        headerName: "Mnc",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        width: 150,
      },
      {
        field: "priority",
        headerName: "Priority",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        width: 100,
      },

      {
        field: "createdAt",
        headerName: "CreatedAt",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "updatedAt",
        headerName: "UpdatedAt",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "Account.displayName",
        headerName: "Created By",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "action",
        headerName: "Actions",
        editable: false,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        width: 100,
        renderCell: ({ row }) => (
          <RoutePlanDropDownOptions
            routePlanIds={[...selectedIds, row.id]}
            routePlanId={row.id}
            routeId={row.routeId}
            onRoutePlanUpdate={onRoutePlanUpdateOpen}
            onRoutePlanDelete={onRoutePlanDeleteOpen}
            onVendorAccountAttachRoutePlanOpen={
              onVendorAccountAttachRoutePlanOpen
            }
            selectedIds={selectedIds}
          />
        ),
      },
    ],
    [selectAll, selectedIds],
  );

  // Rows mapping
  const rows = React.useMemo(
    () =>
      _.compact(
        _rows?.map((item, index) =>
          _successFlags.includes(item?.status)
            ? {
                id: item?.id ?? `route-plan-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
                routeId: item?.Route?.id,
                sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
                "Route.displayName": item?.Route?.displayName ?? "-",
                "VendorAccount.displayName":
                  item?.VendorAccounttDirectory?.displayName ??
                  item?.VendorAccount?.displayName ??
                  "-",
                "Mcc.countryName": item?.Mcc?.countryName ?? "-",
                "Mnc.network": item?.Mnc?.network ?? "-",
                "Mcc.mcc": item?.Mcc?.mcc ?? "-",
                "Mnc.mnc": item?.Mnc?.mnc ?? "-",
                priority: item?.priority ?? "-",
                createdAt: Moment(item?.createdAt).format("YYYY-MM-DD"),
                updatedAt: Moment(item?.updatedAt).format("YYYY-MM-DD"),
                "Account.displayName": item?.Account?.displayName ?? "-",
              }
            : null,
        ),
      ) || [],
    [_rows, paginationModel.page, paginationModel.pageSize],
  );
  const _QueryRoutePlanReadRowCount = _.first(
    _QueryRoutePlanRead?.data?.RoutePlanRead,
  )?._totalCount;
  const _isRoutePlanReadEmpty =
    Array.isArray(_rows) &&
    (0 === _rows.length ||
      _.some(_rows, (item) => "ROUTE_PLAN_NOT_FOUND" === item?.status));
  const rowCount = React.useMemo(() => {
    if (_isRoutePlanReadEmpty) {
      _rowCountRef.current = 0;
    } else if (
      undefined !== _QueryRoutePlanReadRowCount &&
      null !== _QueryRoutePlanReadRowCount
    ) {
      _rowCountRef.current = _QueryRoutePlanReadRowCount;
    }

    return _rowCountRef.current;
  }, [_isRoutePlanReadEmpty, _QueryRoutePlanReadRowCount]);

  // Return component.
  return (
    <>
      <Flex className="routePlan base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          gap={_isCurrentViewMobile ? "12px" : "14px"}
          bg="white"
          height={_isCurrentViewMobile ? "500px" : "100%"}
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          {/* Header Section */}
          <Flex pb="0px" justify="space-between" alignItems="center">
            <Flex display="flex" flexDir="row" gap="12px">
              <Button
                onClick={onRoutePlanCreateOpen}
                leftIcon={<HiPlusCircle />}
                style={buttonStyle}
              >
                New Route Plan
              </Button>
            </Flex>
            <Flex gap="12px">
              <Tooltip label="Refresh routes." fontSize="sm">
                <Button
                  borderRadius="8px"
                  fontWeight="450"
                  minW="20px"
                  backgroundColor="purple.200"
                  color="purple"
                  _hover={{ bg: "purple.800", color: "white" }}
                  leftIcon={<HiArrowPath />}
                  isDisabled={isRefreshing}
                  onClick={_HandleRefresh}
                >
                  Refresh
                </Button>
              </Tooltip>
              <DownloadToExcel
                cellsData={
                  0 === _QueryRoutePlanRead.data?.RoutePlanRead?.length ||
                  !_.every(
                    _.pluck(_QueryRoutePlanRead.data?.RoutePlanRead, "status"),
                    (i) => _successFlags?.includes(i),
                  )
                    ? []
                    : _rows?.map((item, __index) => ({
                        "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                        "Route Name": item?.Route?.displayName ?? "-",
                        "Vendor Account":
                          item?.VendorAccounttDirectory?.displayName ??
                          item?.VendorAccount?.displayName ??
                          "-",
                        "Created By": item?.Account?.displayName ?? "-",
                        "Country Name": item?.Mcc?.countryName ?? "-",
                        Operator: item?.Mnc?.network ?? "-",
                        Mcc: item?.Mcc?.mcc ?? "-",
                        Mnc: item?.Mnc?.mnc ?? "-",
                        Priority: item?.priority ?? "-",
                        CreatedAt: Moment(item?.createdAt).format("YYYY-MM-DD"),
                        UpdatedAt: Moment(item?.updatedAt).format("YYYY-MM-DD"),
                      })) || []
                }
                headersData={[
                  "S.No.",
                  "Route Name",
                  "Vendor Account",
                  "Created By",
                  "Country Name",
                  "Operator",
                  "Mcc",
                  "Mnc",
                  "Priority",
                  "CreatedAt",
                  "UpdatedAt",
                ].map((i) => ({ key: i, label: i }))}
              />
            </Flex>
          </Flex>

          {/* Filter Section */}
          <Flex
            gap="12px"
            flexWrap="wrap"
            bg="white"
            p="10px"
            borderRadius="12px"
            border="1px solid #C5CFE8"
            alignItems="flex-start"
          >
            <Flex
              direction="column"
              minW={{ base: "100%", md: "150px" }}
              w={{ base: "100%", md: "200px" }}
            >
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Mcc
              </Text>
              <MemoizedInput
                placeholder="Search by Mcc"
                className="filter searchInput"
                defaultValue={mccNameToSearch}
                onChange={_onMccNameToSearchInputChange}
              />
            </Flex>

            <Flex
              direction="column"
              minW={{ base: "100%", md: "150px" }}
              w={{ base: "100%", md: "200px" }}
            >
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Mnc
              </Text>
              <MemoizedInput
                placeholder="Search by Mnc"
                className="filter searchInput"
                defaultValue={mncNameToSearch}
                onChange={_onMncNameToSearchInputChange}
              />
            </Flex>
            <Flex
              direction="column"
              minW={{ base: "100%", md: "150px" }}
              w={{ base: "100%", md: "200px" }}
            >
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Country Name
              </Text>
              <MemoizedInput
                placeholder="Search by Country"
                className="filter searchInput"
                defaultValue={countryNameToSearch}
                onChange={_onCountryNameToSearchInputChange}
              />
            </Flex>

            <Flex
              direction="column"
              minW={{ base: "100%", md: "150px" }}
              w={{ base: "100%", md: "200px" }}
            >
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Operator
              </Text>
              <MemoizedInput
                placeholder="Search by Operator"
                className="filter searchInput"
                defaultValue={operatorNameToSearch}
                onChange={_onOperatorNameToSearchInputChange}
              />
            </Flex>
          </Flex>

          {/* MUI DataGrid */}
          <Box
            sx={{
              height: _isCurrentViewMobile
                ? "calc(100vh - 200px)"
                : "calc(100vh - 220px)",
              width: "100%",
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              rowHeight={48}
              rowCount={rowCount}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={_HandlePaginationModelChange}
              filterMode="server"
              filterModel={filterModel}
              onFilterModelChange={_HandleFilterModelChange}
              pageSizeOptions={[10, 30, 50, 100]}
              slots={{
                loadingOverlay: () => (
                  <TableSpinner
                    isLoading={true}
                    isEmpty={false}
                    chopHeightFromHundredPercentage={
                      _tableHeaderHeightRef?.current?.clientHeight
                    }
                  />
                ),
                noResultsOverlay: () => (
                  <TableSpinner
                    isLoading={false}
                    isEmpty={true}
                    chopHeightFromHundredPercentage={
                      _tableHeaderHeightRef?.current?.clientHeight
                    }
                  />
                ),
                noRowsOverlay: () => (
                  <TableSpinner
                    isLoading={false}
                    isEmpty={true}
                    chopHeightFromHundredPercentage={
                      _tableHeaderHeightRef?.current?.clientHeight
                    }
                  />
                ),
              }}
              disableRowSelectionOnClick
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(newModel) =>
                setColumnVisibilityModel(newModel)
              }
              loading={_QueryRoutePlanRead?.loading}
            />
          </Box>
        </Flex>

        {/* Modals */}
        <Modal
          size="3xl"
          title="Route Plan Create"
          isOpen={isRoutePlanCreateOpen}
          onClose={onRoutePlanCreateClose}
          isCentered={false}
        >
          <RoutePlanUpsert
            isCreateOnly={true}
            isOpen={isRoutePlanCreateOpen}
            onClose={onRoutePlanCreateClose}
            refetch={refetch}
          />
        </Modal>
        <Modal
          size="3xl"
          title="Route Plan Update"
          isOpen={isRoutePlanUpdateOpen}
          onClose={onRoutePlanUpdateClose}
          isCentered={false}
        >
          <RoutePlanUpsert
            isOpen={isRoutePlanUpdateOpen}
            onClose={onRoutePlanUpdateClose}
            refetch={refetch}
          />
        </Modal>
        <Modal
          size="md"
          title="Route Plan Delete"
          isOpen={isRoutePlanDeleteOpen}
          onClose={onDeleteModalClose}
        >
          <RoutePlanDelete refetch={refetch} />
        </Modal>
        <Modal
          size="md"
          title="Attach Vendor Account"
          isOpen={isVendorAccountAttachRoutePlanOpen}
          onClose={onVendorAccountAttachRoutePlanClose}
        >
          <VendorAccountAttachRoutePlan refetch={refetch} />
        </Modal>
      </Flex>
    </>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {
  passOn: PropTypes.object,
  PassOnUpdate: PropTypes.func,
};

/*
 * REDUX
 */
const _MapStateToProps = (__state) => ({ passOn: __state.PassOn });
const _MapDispatchToProps = (__dispatch) => ({
  PassOnUpdate: (u) => __dispatch({ type: "PASS_ON_UPDATE", PassOn: u }),
});

/*
 * EXPORT
 */
export default connect(_MapStateToProps, _MapDispatchToProps)(Index);
